import { useCallAPI } from 'hooks/useCallAPI';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ECSegment, ECSegmentFormData, ECSegmentListDTO, ECSegmentType, EventType, MAX_EC_SEGMENTS_OF_ADVERTISER } from 'core/ecSegment/ECSegment';
import { SessionStorageHelper, SessionStorageItemKeys } from 'helper/StorageHelper';
import { DefaultECSegmentManager, ECSegmentManager } from 'core/ecSegment/ECSegmentManager';
import { useParams } from 'react-router-dom';
import i18n from 'i18n';
import { FormField } from 'components/form/FormConfig';
import { useCreateECSegmentFields, useEditECSegmentFields } from './hooks/useECSegmentFields';
import { SelectOptions } from 'components/commonType';
import { partial } from 'lodash';
import { ROUTE_PATH } from 'enum/RoutePath';

type EffectiveModalData = {
  title: string;
  message: string;
  onConfirm: () => void;
};

export type ECSegmentFormModel = {
  loading: boolean;
  redirectPath?: string;
  defaultECSegment: Partial<ECSegmentFormData> | undefined;
  modalData?: EffectiveModalData;
  canAddSegment: boolean;
  save: (values: Partial<ECSegmentFormData>) => void;
  useFields: () => {
    loading: boolean;
    fields: FormField[]
  };
};

const ecSegmentManager: ECSegmentManager = new DefaultECSegmentManager();

export const useCreateECSegmentFormModel = (advertiserOptions: SelectOptions[]): ECSegmentFormModel => {
  const {
    loading,
    callAPIs
  } = useCallAPI();

  const [redirectPath, setRedirectPath] = useState<string>();
  const [canAddSegment, setCanAddSegment] = useState<boolean>(true);

  const cachedAdvertiserId = useMemo(
    () => SessionStorageHelper.getItem(SessionStorageItemKeys.ADVERTISER),
    []
  );

  const onAdvertiserChange = useCallback((advertiserId: number) => {
    callAPIs(
      [() => ecSegmentManager.getECSegments(advertiserId)],
      (ecSegments: ECSegmentListDTO[]) => {
        setCanAddSegment(ecSegments.length < MAX_EC_SEGMENTS_OF_ADVERTISER);
      }
    );
  }, [callAPIs]);

  const defaultECSegment = useMemo(
    () => ({
      name: '',
      type: ECSegmentType.SEARCH_KEYWORDS,
      eventType: EventType.SEARCH,
      advertiserId: cachedAdvertiserId ? +cachedAdvertiserId : undefined,
      inclusion: []
    }),
    [cachedAdvertiserId]
  );

  const save = useCallback((values: Partial<ECSegmentFormData>) => {
    callAPIs([() => ecSegmentManager.createECSegment(values)], () => {
      setRedirectPath('/ec-segments');
    });
  }, [callAPIs]);

  const useFields = useMemo(() => {
    return partial(useCreateECSegmentFields, advertiserOptions, onAdvertiserChange);
  }, [advertiserOptions, onAdvertiserChange]);

  return {
    loading,
    redirectPath,
    canAddSegment,
    defaultECSegment,
    save,
    useFields
  };
};

export const useEditECSegmentFormModel = (advertiserOptions: SelectOptions[]): ECSegmentFormModel => {
  const {
    loading,
    callAPIs
  } = useCallAPI();

  const params = useParams<{ segmentId: string }>();
  const id: string | undefined = params.segmentId;
  const [redirectPath, setRedirectPath] = useState<string>();
  const [defaultECSegment, setDefaultECSegment] = useState<ECSegmentFormData>();
  const [modalData, setModalData] = useState<EffectiveModalData>();

  useEffect(() => {
    if (!id) {
      return;
    }
    callAPIs([() => ecSegmentManager.getECSegment(id)], (ecSegment: ECSegment) => {
      if (ecSegment.type === ECSegmentType.INTERSECT) {
        setRedirectPath(`/ec-segments/${ecSegment.id}/edit/${ROUTE_PATH.ERROR403}`);
      }
      setDefaultECSegment(ecSegment);
    });
  }, [id, callAPIs]);

  const updateECSegment = useCallback((values: Partial<ECSegmentFormData>) => {
    if (!id) {
      return;
    }
    callAPIs([() => ecSegmentManager.updateECSegment(id, values)], () => {
      setRedirectPath('/ec-segments');
    });
  }, [id, callAPIs]);

  const save = useCallback((values: Partial<ECSegmentFormData>) => {
    setModalData({
      title: i18n.t<string>('common.titles.hint'),
      message: i18n.t<string>('ecSegmentForm.hints.effective'),
      onConfirm: () => updateECSegment(values)
    });
  }, [updateECSegment]);

  const useFields = useMemo(() => {
    return partial(useEditECSegmentFields, advertiserOptions);
  }, [advertiserOptions]);

  return {
    loading,
    modalData,
    redirectPath,
    canAddSegment: true,
    defaultECSegment,
    save,
    useFields
  };
};
